import { Cargando } from '../componentes/Cargando'
import { EstadoError } from '../componentes/EstadoError'
import { EstadoVacio } from '../componentes/EstadoVacio'
import { TablaDatos, ayudanteColumnas } from '../componentes/TablaDatos'
import { TAMANO_PAGINA, useAvisos, useResumenAvisos, type AvisoResumen } from '../hooks/useAvisos'
import { useFiltrosAvisos } from '../hooks/useFiltrosAvisos'
import { BarraFiltrosAvisos } from './avisos/BarraFiltrosAvisos'
import { InsigniaEstado } from './avisos/InsigniaEstado'
import { MarcaSemanas } from './avisos/MarcaSemanas'
import { TarjetaAviso } from './avisos/TarjetaAviso'
import { etiquetaEstadoPlural, formatearFechaCorta, formatearMedia } from './avisos/formato'

const ayudante = ayudanteColumnas<AvisoResumen>()

/**
 * Las columnas de fecha llevan en el acceso la cadena ISO cruda, no el texto ya
 * formateado: "01/02/2026" ordenado como texto pone enero de 2026 junto a enero
 * de 2019, mientras que el ISO ordena bien tal cual. El formato vive en `cell`.
 */
const COLUMNAS = ayudante.columns([
  ayudante.accessor('nombre', { header: 'Nombre' }),
  ayudante.accessor((fila) => fila.telefono ?? '', {
    id: 'telefono',
    header: 'Telefono',
    cell: (info) => info.getValue() || 'Sin telefono',
  }),
  ayudante.accessor('semanas_sin_pedir', {
    header: 'Semanas sin pedir',
    cell: (info) => <MarcaSemanas semanas={info.getValue()} />,
  }),
  ayudante.accessor((fila) => fila.fecha_ultimo_albaran ?? '', {
    id: 'fecha_ultimo_albaran',
    header: 'Ultimo albaran',
    cell: (info) => formatearFechaCorta(info.getValue()) ?? 'Sin albaran',
  }),
  ayudante.accessor('estado', {
    header: 'Estado',
    cell: (info) => <InsigniaEstado estado={info.getValue()} />,
  }),
  ayudante.accessor((fila) => fila.fecha_ultimo_aviso ?? '', {
    id: 'fecha_ultimo_aviso',
    header: 'Ultimo aviso',
    // "Sin avisar" y no un hueco en blanco: 48 de los 295 clientes reales estan
    // asi, y una celda vacia se lee como un fallo de carga.
    cell: (info) => formatearFechaCorta(info.getValue()) ?? 'Sin avisar',
  }),
  ayudante.accessor('aplazamientos_consecutivos', { header: 'Aplazamientos' }),
])

function Metrica({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div className="rounded-xl border border-borde bg-superficie px-4 py-3">
      <dt className="text-xs font-semibold uppercase tracking-wide text-tinta-apagada">{etiqueta}</dt>
      <dd className="mt-1 text-2xl font-semibold text-tinta">{valor}</dd>
    </div>
  )
}

/** Mientras el resumen viaja no se inventa un cero, que se leeria como un dato. */
const SIN_DATO = '--'

export function Avisos() {
  const { filtros, cambiarFiltro, irAPagina } = useFiltrosAvisos()
  const avisos = useAvisos(filtros)
  const resumen = useResumenAvisos()

  const filas = avisos.data?.items ?? []
  // El total es el que devuelve la api, no `filas.length`: con 200 por pagina
  // contar filas mentiria en cuanto haya mas de una pagina.
  const total = avisos.data?.total ?? 0
  const tamanoPagina = avisos.data?.page_size || TAMANO_PAGINA
  const pagina = avisos.data?.page ?? filtros.page
  const paginas = Math.max(1, Math.ceil(total / tamanoPagina))

  const metricas = resumen.data
  const numero = (valor: number | undefined) => (valor === undefined ? SIN_DATO : String(valor))

  const activos: string[] = []
  if (filtros.q) activos.push(`busqueda "${filtros.q}"`)
  if (filtros.estado) activos.push(etiquetaEstadoPlural(filtros.estado))
  if (filtros.semanas_minimas !== undefined) {
    activos.push(`${filtros.semanas_minimas} semanas o mas sin pedir`)
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-xl font-semibold text-tinta">Avisos</h1>
        <p className="mt-1 text-sm text-tinta-apagada">
          Seguimiento de los clientes que llevan tiempo sin pedir. Los que mas tiempo llevan salen
          primero.
        </p>
      </div>

      {/* Region con nombre y no un `<header>`: la pagina vive dentro del `<main>`
          del shell, donde un `<header>` no es la cabecera de la pagina. */}
      <section aria-label="Metricas del seguimiento">
        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <Metrica etiqueta="En seguimiento" valor={numero(metricas?.total)} />
          <Metrica etiqueta="Pendientes" valor={numero(metricas?.pendientes)} />
          <Metrica etiqueta="Avisados" valor={numero(metricas?.avisados)} />
          <Metrica etiqueta="Con aplazamientos" valor={numero(metricas?.con_aplazamientos)} />
          <Metrica
            etiqueta="Media sin pedir"
            valor={
              metricas === undefined
                ? SIN_DATO
                : `${formatearMedia(metricas.media_semanas_sin_pedir)} semanas`
            }
          />
        </dl>
        {resumen.isError ? (
          // Sin `role="alert"`: el listado, que es el trabajo, sigue ahi. Una
          // alerta que interrumpe al lector de pantalla por unas metricas
          // ausentes cuesta mas de lo que informa.
          <p className="mt-2 text-sm text-tinta-apagada">
            No se han podido cargar las metricas de cabecera. El seguimiento se sigue viendo.
          </p>
        ) : null}
      </section>

      <section aria-label="Seguimiento de avisos" className="flex min-w-0 flex-col gap-4">
        <BarraFiltrosAvisos filtros={filtros} onCambiar={cambiarFiltro} />

        {avisos.isPending ? (
          <Cargando mensaje="Cargando el seguimiento" />
        ) : avisos.isError ? (
          <EstadoError error={avisos.error} onReintentar={() => void avisos.refetch()} />
        ) : filas.length === 0 ? (
          <EstadoVacio
            titulo="No hay clientes con estos filtros"
            descripcion={
              activos.length > 0
                ? `Ningun cliente coincide con los filtros activos: ${activos.join(', ')}.`
                : 'El seguimiento ha llegado vacio de la API.'
            }
            accion={
              filtros.q ? (
                <button
                  type="button"
                  onClick={() => cambiarFiltro('q', undefined)}
                  className="rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm font-medium text-tinta-2 transition-colors hover:bg-elevado"
                >
                  Limpiar la busqueda
                </button>
              ) : null
            }
          />
        ) : (
          <>
            {/* Tabla solo en escritorio. */}
            <div className="hidden lg:block">
              <TablaDatos
                datos={filas}
                columnas={COLUMNAS}
                estadoVacio={null}
                etiqueta="Avisos de clientes sin pedir"
              />
            </div>

            {/* Tarjetas apiladas en movil: siete columnas a 375 px obligarian a
                desplazarse en horizontal para leer un solo cliente. */}
            <ul aria-label="Avisos en tarjetas" className="flex flex-col gap-2 lg:hidden">
              {filas.map((aviso) => (
                <TarjetaAviso key={aviso.id} aviso={aviso} />
              ))}
            </ul>

            <nav
              aria-label="Paginacion"
              className="flex items-center justify-between gap-3 text-sm"
            >
              <button
                type="button"
                aria-label="Pagina anterior"
                disabled={pagina <= 1}
                onClick={() => irAPagina(pagina - 1)}
                className="rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 font-medium text-tinta-2 transition-colors hover:bg-elevado disabled:cursor-not-allowed disabled:border-borde disabled:text-desactivado-tinta"
              >
                Anterior
              </button>
              <span className="text-tinta-apagada">
                Pagina {pagina} de {paginas}
              </span>
              <button
                type="button"
                aria-label="Pagina siguiente"
                disabled={pagina >= paginas}
                onClick={() => irAPagina(pagina + 1)}
                className="rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 font-medium text-tinta-2 transition-colors hover:bg-elevado disabled:cursor-not-allowed disabled:border-borde disabled:text-desactivado-tinta"
              >
                Siguiente
              </button>
            </nav>
          </>
        )}
      </section>
    </div>
  )
}
